// NPM
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil, omitBy } from 'lodash';
import { useRecoilState } from 'recoil';

// ANT COMPONENT
import { AlertBox, Button, TextInput} from '@life-line-lab/components-react';
import { Space, Form, Input } from 'antd';

// ATOM
import { emailAtom } from 'Recoil/email.atom';

// UTIL
import errorHandler from 'Utils/error-handler';

// SERVICE
import { sendCancelOrderEmail } from 'Services/email.services';

// VALIDATOR
import CancelFormSchema from './cancel-order-form.validation';

// STYLE
import './cancel-order-form.scss';

interface InputInterface {
  fullName: string;
  bankName: string;
  bankAccNumber: string;
  orderNumber: string;
  productName: string;
  reason: string;
}

interface ErrorInterface {
  [x: string]: any
}

interface CancelOrderFormProps {
  cancelStatus: string;
  orderNumber: string;
  productName: string;
  onCloseModal: () => void;
  confirmCancel: () => void;
}

const { TextArea } = Input;

const CancelForm: React.FC<CancelOrderFormProps> = ({ cancelStatus, onCloseModal, confirmCancel, orderNumber, productName }) => {
  const MAX_LIMIT = 1000;

  const initialError = {}
  const { t } = useTranslation();
  
  const elRef = React.useRef<HTMLHeadingElement>(null);
  const scrollToRef = (ref: any) => ref.current.scrollIntoView(false);

  const [state, setState] = useRecoilState(emailAtom);
  const [error, setError] = useState<ErrorInterface>(initialError);
  const [input, setInput] = useState<InputInterface>({ 
    fullName: '',
    bankName: '',
    bankAccNumber: '',
    orderNumber: orderNumber || '',
    productName: productName || '',
    reason: ''
  });

  useEffect(() => {
    if (cancelStatus === 'error' && !isEmpty(error)) {
      console.log(error)

      if (error.type === 'VALIDATION_ERROR') {
        if (error.stack?.field) {
          const value = error.value;
          const field = error.stack.field;

          setError({ ...error, [field]: t(`${value}`, { field: t(`label.${field}`) }) });
          (document.querySelector(`input[name=${error.stack.field}]`) as HTMLInputElement).focus();
        }
        else {
          setError({ ...error, general: t(error.value) });
        }
      }
      else {
        setError({ ...error, general: t(error.value) });
        scrollToRef(elRef);
      }
    }
  }, [cancelStatus, error]);

  useEffect(() => {
    if (cancelStatus === 'success' && state.status.send === 'success') {
      onCloseModal();
    }
  }, [cancelStatus, state.status]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      const value: any = CancelFormSchema.validateSync(omitBy({
        ...input
      }, isNil), {
        strict: true,
        abortEarly: true,
        stripUnknown: false
      });

      if (!isEmpty(value)) {
        confirmCancel && confirmCancel();
        // Submit Email
        setState(prevState => ({ 
          ...prevState, 
          status: { ...prevState.status, send: 'loading' } 
        }));

        const res = await sendCancelOrderEmail(value);
        if (res.error) {
          setState(prevState => ({
            ...prevState,
            error: { message: 'toaster.supportFailed' },
            status: { ...prevState.status, send: 'error' }
          }));

          return;
        }
        
        setState(prevState => ({ 
          ...prevState, 
          status: { ...prevState.status, send: 'success' } 
        }));
      }
    }
    catch (err) {
      let { path, refPath, value } = errorHandler.validation(err) || {};

      if (!isNil(refPath)) {
        setError({
          ...error,
          [path]: t(`${value}`, {
            field: t(`label.${path}`),
            refPath: refPath ? t(`label.${refPath}`) : null
          })
        });
      }
      else {
        if (path.indexOf('.') !== -1) {
          const str = path.split('.');
          path = str[0];
        }
        setError({
          ...error,
          [path]: t(`${value}`, { field: t(`label.${path}`) })
        });
      }

      if (document.querySelector(`input[name=${path}]`)) {
        (document.querySelector(`input[name=${path}]`) as HTMLInputElement).focus();
      }
      else if (document.querySelector(`div[name=${path}]`)) {
        (document.querySelector(`div[name=${path}] input`) as HTMLInputElement).focus();
      }
    }
  }

  return (
    <form className="cancel-order-form">
      <Space direction="vertical" size="large">
        <div ref={elRef}>
          <AlertBox isFilled={true} text={error.general} />
        </div>

        <div>
          <TextInput
            isError={!!error.fullName}
            isRequired={true}
            label={t('label.fullName')}
            name="fullName"
            value={input.fullName}
            onChange={handleChange}
          />
          <AlertBox text={error.fullName} />
        </div>

        <div>
          <TextInput
            isError={!!error.bankName}
            isRequired={true}
            label={t('label.bankName')}
            name="bankName"
            value={input.bankName}
            onChange={handleChange}
          />
          <AlertBox text={error.bankName} />
        </div>

        <div>
          <TextInput
            isError={!!error.bankAccNumber}
            isRequired={true}
            label={t('label.bankAccNumber')}
            name="bankAccNumber"
            value={input.bankAccNumber}
            onChange={handleChange}
          />
          <AlertBox text={error.bankAccNumber} />
        </div>

        <div>
          <TextInput
            isError={!!error.orderNumber}
            isRequired={true}
            label={t('label.orderNumber')}
            name="orderNumber"
            value={input.orderNumber}
            isReadonly={true}
          />
          <AlertBox text={error.orderNumber} />
        </div>

        <div>
          <TextInput
            isError={!!error.productName}
            isRequired={true}
            label={t('label.productName')}
            name="productName"
            value={input.productName}
            isReadonly={true}
          />
          <AlertBox text={error.productName} />
        </div>

        <div>
          <Form.Item
            className="flex-column"
            requiredMark={true}
            required={true}
            style={{
              textAlign: 'start',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '0.02rem',
            }}
            label={t('label.reason')}
            help={(
              <small className="right">
                {`${input.reason.length} / 1000`}
              </small>
            )}
          >
            <TextArea
              name="reason"
              size="middle"
              value={input.reason}
              onChange={handleChange}
              rows={10}
              required={true}
              maxLength={MAX_LIMIT}
            />
          </Form.Item>

          <AlertBox text={error.reason} />
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0 10px' }}>
          <Button
            isLoading={cancelStatus === "loading"}
            className="cancel-button"
            type="default"
            label={
              <React.Fragment>
                <span>{t('action.back')}</span>
              </React.Fragment>
            }
            size="large"
            onClick={onCloseModal}
          />
          <Button
            isLoading={cancelStatus === "loading"}
            className="cancel-button"
            type="primary"
            label={
              <React.Fragment>
                <span>{t('action.continue')}</span>
              </React.Fragment>
            }
            size="large"
            onClick={handleSubmit}
          />
        </div>
      </Space>
    </form>
  )
}

export default CancelForm;